import { AnimatedBox } from '@apeiron/temp'
import { ApeironBattleDemoTitle } from '@apeiron/ui'
import { styled, useMediaQuery } from '@mui/material'
import ImageBackground from '@public/images/background-battle-demo.jpg'
import ImageBottomLeft from '@public/images/image-battle-demo-left-bottom.png'
import ImageTopLeft from '@public/images/image-battle-demo-left-top.png'
import ImageMobileBottomLeft from '@public/images/image-battle-demo-mobile-left-bottom.png'
import ImageMobileTopLeft from '@public/images/image-battle-demo-mobile-left-top.png'
import ImageMobileBottomRight from '@public/images/image-battle-demo-mobile-right-bottom.png'
import ImageMobileTopRight from '@public/images/image-battle-demo-mobile-right-top.png'
import ImageBottomRight from '@public/images/image-battle-demo-right-bottom.png'
import ImageTopRight from '@public/images/image-battle-demo-right-top.png'
import BattleDemoCheaterWarning from '@src/components/battle/demo/leaderboard/BattleDemoCheaterWarning'
import BattleDemoSoloPvELeaderboard from '@src/components/battle/demo/leaderboard/BattleDemoSoloPvELeaderboard'
import BattleDemoSecNavBar from '@src/components/navigationBar/secondary/BattleDemoSecNavBar'
import ApeironCorneredBox from '@src/components/share/apeiron/ApeironCorneredBox'
import { ROUTE } from '@src/constants/route'
import { useSecondaryNavBarContext } from '@src/contexts/share/SecondaryNavBarContext'
import theme from '@src/styles/theme'
import type { NextPage } from 'next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(ApeironCorneredBox)`
  height: calc(100vh - 116px);
  width: 100%;

  ${props => props.theme.breakpoints.up('res1024')} {
    background-image: url(${ImageBackground.src});
    background-size: cover;
    background-position: center;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)),
      url(${ImageBackground.src});
    background-size: cover;
    background-position: center bottom 60%;
  }

  .Custom-Container {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
  .Custom-Corner-Container {
    transform: rotate(180deg);
  }
  .Custom-Top-Left {
    aspect-ratio: 0.52;
    max-width: 150px;
    width: 100%;
  }
  .Custom-Bottom-Left {
    aspect-ratio: 0.545;
    max-width: 350px;
    width: 100%;
  }
  .Custom-Top-Right {
    aspect-ratio: 0.545;
    max-width: 120px;
    width: 100%;
  }
  .Custom-Bottom-Right {
    aspect-ratio: 1.755;
    bottom: -55px;
    max-width: 1000px;
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    .Custom-Top-Left {
      aspect-ratio: 0.883;
      max-width: 218px;
      width: 100%;
    }
    .Custom-Bottom-Left {
      aspect-ratio: 0.382;
      max-width: 42px;
      width: 100%;
    }
    .Custom-Top-Right {
      aspect-ratio: 0.765;
      max-width: 85px;
      width: 100%;
    }
    .Custom-Bottom-Right {
      aspect-ratio: 0.742;
      max-width: 319px;
      width: 100%;
    }
  }
`

const StyledBattleDemoTitle = styled(ApeironBattleDemoTitle)`
  ${props => props.theme.breakpoints.up('res1024')} {
    margin-top: 45px;
    margin-left: 90px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    margin-top: 40px;
    margin-left: 20px;
  }
`

const FadeInContainer = styled(AnimatedBox)`
  display: flex;
  flex: 1;
  justify-content: center;
  margin: 40px 0px 0px 0px;
  width: 100%;
`

const Leaderboard = styled(BattleDemoSoloPvELeaderboard)`
  min-height: 826px;
`

const SoloPvEPage: NextPage<Props> = () => {
  const { setupBar } = useSecondaryNavBarContext()

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const [cornerAnimated, setCornerAnimated] = useState<boolean>(false)

  const handleOnAnimationEnd = useCallback(() => {
    setCornerAnimated(true)
  }, [])

  useEffect(() => {
    setupBar(<BattleDemoSecNavBar route={ROUTE.BATTLE_DEMO_SOLO_PVE} />)

    return () => setupBar(null)
  }, [setupBar])

  return (
    <Container
      bottomLeft={mobile ? ImageMobileBottomLeft.src : ImageBottomLeft.src}
      bottomRight={mobile ? ImageMobileBottomRight.src : ImageBottomRight.src}
      topLeft={mobile ? ImageMobileTopLeft.src : ImageTopLeft.src}
      topRight={mobile ? ImageMobileTopRight.src : ImageTopRight.src}
      onAnimationEnd={handleOnAnimationEnd}
    >
      <StyledBattleDemoTitle
        title={t('battle_demo.leaderboard.page_title')}
        subtitle={t('battle_demo.leaderboard.description')}
      />
      <FadeInContainer in={cornerAnimated}>
        <Leaderboard />
      </FadeInContainer>
      <BattleDemoCheaterWarning />
    </Container>
  )
}

type Props = {}

export default SoloPvEPage
